import { useState } from 'react';
import { User, LogOut, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  user: any;
  activeTab: 'gpts' | 'client-gpts' | 'prompts';
  onTabChange: (tab: 'gpts' | 'client-gpts' | 'prompts') => void;
  onAdminClick: () => void;
  onPasswordChange: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  user, 
  activeTab, 
  onTabChange, 
  onAdminClick,
  onPasswordChange 
}) => {
  const { logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <header className="bg-[#21282D] shadow-sm border-b border-gray-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Title */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full overflow-hidden">
                <img src="/red-rocket.png" alt="Rocket Logo" className="w-full h-full object-cover" />
              </div>
              <h1 className="text-xl font-bold text-gray-100">Hunterlodge GPT Launcher</h1>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center space-x-8">
            <button
              onClick={() => onTabChange('gpts')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'gpts'
                  ? 'text-red-400 border-b-2 border-red-400'
                  : 'text-gray-300 hover:text-gray-100'
              }`}
            >
              GPTs
            </button>
            <button
              onClick={() => onTabChange('client-gpts')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'client-gpts'
                  ? 'text-red-400 border-b-2 border-red-400'
                  : 'text-gray-300 hover:text-gray-100'
              }`}
            >
              Client GPTs
            </button>
            <button
              onClick={() => onTabChange('prompts')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'prompts'
                  ? 'text-red-400 border-b-2 border-red-400'
                  : 'text-gray-300 hover:text-gray-100'
              }`}
            >
              Prompt Library
            </button>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Admin Button */}
            {user?.role === 'admin' && (
              <button
                onClick={onAdminClick}
                className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 transition-colors duration-200"
              >
                Admin Panel
              </button>
            )}

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-100">
                  {user?.name || user?.email}
                </span>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-[#21282D] rounded-lg shadow-lg border border-gray-600 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-600">
                    <p className="text-sm font-medium text-gray-100">{user?.name}</p>
                    <p className="text-xs text-gray-400">{user?.email}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
                  </div>
                  
                  <button
                    onClick={() => {
                      onPasswordChange();
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-100 hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <Lock className="w-4 h-4" />
                    <span>Change Password</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-100 hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 